import { motion } from 'framer-motion';
import { ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';

const Section = ({ title, children }) => (
  <div className="mb-8">
    {title && <h2 className="text-2xl font-black text-white mb-3">{title}</h2>}
    <div className="text-gray-300 leading-relaxed space-y-3">{children}</div>
  </div>
);

const List = ({ items }) => (
  <ul className="list-disc pl-6 space-y-1 text-gray-400">
    {items.map((item, i) => (
      <li key={i}>{item}</li>
    ))}
  </ul>
);

const TermsOfService = () => {
  return (
    <div className="min-h-screen bg-black text-white py-16 px-4" data-testid="terms-of-service-page">
      <div className="max-w-3xl mx-auto">
        <Link to="/">
          <Button
            variant="ghost"
            className="text-gray-400 hover:text-white mb-8"
            data-testid="terms-back-button"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Home
          </Button>
        </Link>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <h1 className="text-4xl sm:text-5xl font-black text-transparent bg-clip-text bg-gradient-to-r from-purple-500 via-purple-600 to-purple-700 mb-2">
            Terms of Service
          </h1>
          <p className="text-gray-500 mb-10">Last Updated: July 23, 2026</p>

          <Section>
            <p>
              Welcome to Quantum Shop. These Terms of Service ("Terms") govern your access to and use
              of the Quantum Shop website, services, and online store (collectively, the "Service").
            </p>
            <p>
              By accessing or using Quantum Shop, you agree to these Terms. If you do not agree with
              these Terms, please do not use our Service.
            </p>
          </Section>

          <Section title="1. Eligibility">
            <p>
              You must meet the minimum age required to use online services in your jurisdiction to
              use Quantum Shop.
            </p>
            <p>
              If you are under the age required to enter into a legally binding agreement, you may
              only use the Service with the permission and supervision of a parent or legal guardian.
            </p>
          </Section>

          <Section title="2. Our Services">
            <p>
              Quantum Shop provides an online marketplace for digital game items and related digital
              products.
            </p>
            <p>
              Digital items purchased through Quantum Shop may be delivered electronically to the
              game account or username provided by the customer, depending on the product purchased.
            </p>
            <p>
              Quantum Shop is not affiliated with, endorsed by, or officially associated with any
              game developer, publisher, or platform unless explicitly stated.
            </p>
          </Section>

          <Section title="3. Accounts">
            <p>Some features of Quantum Shop may require you to create an account.</p>
            <p>You are responsible for:</p>
            <List
              items={[
                'Providing accurate information',
                'Keeping your login credentials secure',
                'Maintaining the security of your account',
                'All activity that occurs through your account',
              ]}
            />
            <p>
              You must not share your account credentials with others or use another person's
              account without permission.
            </p>
            <p>
              Quantum Shop reserves the right to suspend or terminate accounts that violate these
              Terms.
            </p>
          </Section>

          <Section title="4. Purchases and Payments">
            <p>When placing an order, you agree to provide accurate and complete information.</p>
            <p>All prices displayed on Quantum Shop are subject to change without notice.</p>
            <p>
              Payment transactions may be processed by third-party payment providers. By completing a
              purchase, you agree to comply with the applicable terms and conditions of the payment
              provider.
            </p>
            <p>
              An order is considered complete once payment has been successfully processed and the
              purchased digital item has been delivered or made available to the customer.
            </p>
          </Section>

          <Section title="5. Digital Products">
            <p>
              Digital products are generally delivered electronically and do not require physical
              shipping.
            </p>
            <p>
              Customers are responsible for providing accurate account information, usernames, player
              IDs, or other information necessary for delivery.
            </p>
            <p>
              Quantum Shop is not responsible for delays or failed deliveries caused by incorrect
              information provided by the customer.
            </p>
            <p>
              We do not guarantee that digital items will remain available or usable if a game
              developer, publisher, or platform changes, removes, or restricts the relevant game or
              service.
            </p>
          </Section>

          <Section title="6. Refunds and Cancellations">
            <p>
              Because digital products may be delivered immediately, refunds may not be available
              after an order has been successfully delivered or redeemed.
            </p>
            <p>
              If you experience an issue with your order, you should contact Quantum Shop customer
              support as soon as possible.
            </p>
            <p>Refund requests may be reviewed on a case-by-case basis.</p>
            <p>
              Nothing in these Terms limits any refund or consumer rights that cannot legally be
              excluded under applicable law.
            </p>
          </Section>

          <Section title="7. Prohibited Activities">
            <p>You agree not to:</p>
            <List
              items={[
                'Use Quantum Shop for unlawful purposes',
                'Provide false or misleading information',
                'Attempt to commit fraud or conduct unauthorized transactions',
                'Abuse payment systems or chargeback processes',
                'Attempt to gain unauthorized access to Quantum Shop accounts or systems',
                'Interfere with or disrupt the operation of the Service',
                'Use automated systems to abuse or overload the website',
                'Resell products in violation of applicable laws or third-party rules',
                'Use Quantum Shop to violate the rules or terms of a game, platform, or service',
              ]}
            />
            <p>
              Quantum Shop reserves the right to suspend or terminate accounts involved in prohibited
              activities.
            </p>
          </Section>

          <Section title="8. Intellectual Property">
            <p>
              All content on Quantum Shop, including logos, branding, website design, text, graphics,
              and original materials, is owned by or licensed to Quantum Shop unless otherwise stated.
            </p>
            <p>
              You may not copy, reproduce, modify, distribute, or commercially exploit Quantum Shop's
              content without permission.
            </p>
            <p>
              All third-party game names, trademarks, logos, and intellectual property belong to
              their respective owners.
            </p>
          </Section>

          <Section title="9. Third-Party Games and Platforms">
            <p>
              Quantum Shop may provide products or services related to third-party games and
              platforms.
            </p>
            <p>
              Quantum Shop does not control third-party games or platforms and cannot guarantee that
              purchased items will always remain available, transferable, or usable.
            </p>
            <p>
              Changes to third-party games, platforms, servers, policies, or account systems may
              affect the availability or functionality of purchased items.
            </p>
            <p>
              Customers are responsible for following the rules and terms of the relevant game or
              platform.
            </p>
          </Section>

          <Section title="10. Service Availability">
            <p>
              We make reasonable efforts to keep Quantum Shop available and functioning properly.
              However, we do not guarantee that the Service will always be available, uninterrupted,
              secure, or error-free.
            </p>
            <p>
              We may temporarily suspend or modify the Service for maintenance, security, updates, or
              other reasons.
            </p>
          </Section>

          <Section title="11. Account Suspension and Termination">
            <p>Quantum Shop may suspend or terminate your access to the Service if you:</p>
            <List
              items={[
                'Violate these Terms',
                'Engage in fraudulent activity',
                'Abuse the Service',
                'Attempt to compromise the security of the Service',
                'Engage in unlawful activity',
              ]}
            />
            <p>
              If your account is terminated because of a violation of these Terms, you may lose
              access to your account and certain services.
            </p>
          </Section>

          <Section title="12. Disclaimer">
            <p>
              To the maximum extent permitted by applicable law, Quantum Shop provides its Service on
              an "as is" and "as available" basis.
            </p>
            <p>
              We do not guarantee that the Service or any digital product will always be available,
              uninterrupted, secure, or free from errors.
            </p>
            <p>
              Quantum Shop is not responsible for problems caused by third-party games, platforms,
              internet service providers, payment processors, or other services outside our
              reasonable control.
            </p>
          </Section>

          <Section title="13. Limitation of Liability">
            <p>
              To the maximum extent permitted by applicable law, Quantum Shop and its owners,
              operators, employees, and service providers will not be liable for indirect,
              incidental, special, or consequential damages arising from your use of the Service.
            </p>
            <p>
              Nothing in these Terms excludes liability that cannot legally be excluded or limited
              under applicable law.
            </p>
          </Section>

          <Section title="14. Changes to These Terms">
            <p>We may update these Terms from time to time.</p>
            <p>
              When changes are made, we will update the "Last Updated" date at the top of this
              document. Your continued use of Quantum Shop after updated Terms become effective means
              that you accept the revised Terms.
            </p>
          </Section>

          <Section title="15. Governing Law">
            <p>
              These Terms will be governed by the applicable laws of the jurisdiction in which
              Quantum Shop operates, unless otherwise required by applicable law.
            </p>
            <p>Any disputes will be handled in accordance with applicable laws and regulations.</p>
          </Section>

          <Section title="16. Contact Us">
            <p>
              If you have questions about these Terms or need assistance with an order, please contact
              us at:
            </p>
            <div className="mt-4 p-4 bg-zinc-900 rounded-xl border border-purple-900/30">
              <p className="font-bold text-white">Quantum Shop</p>
              <p>Email: Chingstocks1@gmail.com</p>
              <p>Website: Quantumshop.org</p>
            </div>
            <p className="mt-4">
              By using Quantum Shop, you acknowledge that you have read, understood, and agreed to
              these Terms of Service.
            </p>
          </Section>
        </motion.div>
      </div>
    </div>
  );
};

export default TermsOfService;
